<script>
	import { onMount } from 'svelte';
	import { get } from 'svelte/store';
	import { page } from '$app/stores';
	import { t } from '$lib/i18n.js';
	import { foregroundMessage } from '$lib/services/push-notifications.js';
	import { notificationLinkToWebUrl } from '$lib/services/notification-link.js';

	let current = $state(null);
	let timer = null;

	function dismiss() {
		current = null;
		if (timer) {
			clearTimeout(timer);
			timer = null;
		}
	}

	// Replica _handleDeeplLink dell'app: mappa il link verso un URL web e naviga.
	function open() {
		const link = current?.link;
		dismiss();
		if (!link) return;
		const lang = get(page)?.params?.lang || 'it';
		const url = notificationLinkToWebUrl(link, { lang });
		if (url) window.location.href = url;
	}

	onMount(() => {
		const unsub = foregroundMessage.subscribe((msg) => {
			if (!msg) return;
			current = msg;
			if (timer) clearTimeout(timer);
			timer = setTimeout(dismiss, 12000);
		});
		return () => {
			unsub();
			if (timer) clearTimeout(timer);
		};
	});
</script>

{#if current}
	<div
		class="push-overlay"
		role="dialog"
		aria-modal="true"
		aria-label={current.title}
		tabindex="-1"
		onclick={dismiss}
		onkeydown={(e) => e.key === 'Escape' && dismiss()}
	>
		<div class="push-card" onclick={(e) => e.stopPropagation()}>
			<button
				class="toast-close"
				type="button"
				aria-label="Close"
				onclick={(e) => {
					e.stopPropagation();
					dismiss();
				}}
			>✕</button>

			{#if current.image}
				<img class="toast-image" src={current.image} alt="" />
			{/if}

			<div class="toast-content">
				<h3 class="toast-title">{current.title}</h3>
				{#if current.body}
					<!-- Corpo come HTML, come fa l'app (HtmlWidget). Sorgente: backend Auting (fidato). -->
					<div class="toast-text">{@html current.body}</div>
				{/if}
			</div>

			{#if current.link}
				<button class="toast-action" type="button" onclick={open}>
					{$t('components.nav.push_open')}
				</button>
			{/if}
		</div>
	</div>
{/if}

<style>
	.push-overlay {
		position: fixed;
		inset: 0;
		z-index: 5000;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 24px;
		background: rgba(0, 0, 0, 0.35);
		backdrop-filter: blur(3px);
		animation: overlayIn 0.2s ease;
	}

	@keyframes overlayIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	.push-card {
		position: relative;
		width: 100%;
		max-width: 420px;
		background: var(--surface, #ffffff);
		border: 2px solid var(--orange, #ff6a00);
		border-radius: 24px;
		box-shadow: 0 24px 64px rgba(0, 0, 0, 0.28);
		padding: 28px;
		display: flex;
		flex-direction: column;
		gap: 20px;
		animation: cardIn 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);
	}

	@keyframes cardIn {
		from {
			opacity: 0;
			transform: scale(0.94) translateY(8px);
		}
		to {
			opacity: 1;
			transform: scale(1) translateY(0);
		}
	}

	.toast-image {
		width: 100%;
		max-height: 220px;
		object-fit: cover;
		border-radius: 16px;
		display: block;
	}

	.toast-content {
		display: flex;
		flex-direction: column;
		gap: 10px;
		padding: 0 4px;
	}

	.toast-title {
		margin: 0;
		font-size: 1.5rem;
		font-weight: 700;
		line-height: 1.25;
		color: var(--on-surface, #1a1a1a);
	}

	.toast-text {
		margin: 0;
		font-size: 1.05rem;
		line-height: 1.55;
		color: #4a4a4a;
	}

	.toast-text :global(p) {
		margin: 0 0 8px;
	}

	.toast-action {
		align-self: stretch;
		border: none;
		border-radius: 12px;
		background: var(--orange, #ff6a00);
		color: var(--on-primary, #ffffff);
		font-size: 1.05rem;
		font-weight: 600;
		padding: 12px 16px;
		cursor: pointer;
		transition: filter 0.2s ease;
	}

	.toast-action:hover {
		filter: brightness(0.95);
	}

	.toast-close {
		position: absolute;
		top: 14px;
		right: 14px;
		width: 36px;
		height: 36px;
		border: none;
		border-radius: 50%;
		background: rgba(0, 0, 0, 0.06);
		color: #555;
		font-size: 1.1rem;
		line-height: 1;
		cursor: pointer;
		transition: background 0.2s ease;
		z-index: 1;
	}

	.toast-close:hover {
		background: rgba(0, 0, 0, 0.14);
		color: #222;
	}

	@media (max-width: 480px) {
		.push-card {
			padding: 20px;
			border-radius: 20px;
		}

		.toast-title {
			font-size: 1.3rem;
		}
	}
</style>
